import IdCell from 'composites/IdCell';
import DefaultCell from 'composites/DefaultCell';
import StatusCell from 'composites/StatusCell';
import CustomerCell from 'composites/CustomerCell';

import moment from 'moment';
import { Button, IconButton } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { NavLink } from 'react-router-dom';
import { PaymentStatusResponse } from '../../../packages/crypto-payments/src/models/types';
import { useState } from 'react';
import PaymentDetailsModal from './components/PaymentDetailsModal';

type Column = {
  Header: string;
  accessor: keyof PaymentStatusResponse | string;
  Cell: ({
    value,
    row,
  }: {
    value: any;
    row: { original: PaymentStatusResponse };
  }) => JSX.Element;
};

const PaymentDetailsButton = ({
  payment,
}: {
  payment: PaymentStatusResponse;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <MDButton variant="gradient" color="info" onClick={handleOpenModal}>
        Show
      </MDButton>
      <PaymentDetailsModal
        open={isModalOpen}
        onClose={handleCloseModal}
        payment={payment}
      />
    </>
  );
};

const columns: Column[] = [
  {
    Header: 'id',
    accessor: 'id',
    Cell: ({ value }: any) => <IdCell id={value} />,
  },
  {
    Header: 'Date',
    accessor: 'createdAt',
    Cell: ({ value }: { value: string }) => (
      <DefaultCell value={moment(value).format('DD/MM/YYYY HH:mm')} />
    ),
  },
  {
    Header: 'Transaction ID',
    accessor: 'paymentId',
    Cell: ({
      value,
      row,
    }: {
      value?: string;
      row: { original: PaymentStatusResponse };
    }) => <DefaultCell value={value} />,
  },
  {
    Header: 'Currency',
    accessor: 'currency',
    Cell: ({ value }: { value: string }) => <DefaultCell value={value} />,
  },
  {
    Header: 'Amount',
    accessor: 'amount',
    Cell: ({ value }: { value: number }) => (
      <DefaultCell value={value.toString()} />
    ),
  },
  {
    Header: 'Status',
    accessor: 'status',
    Cell: ({ value }: { value: string }) => {
      let status;
      switch (value) {
        case 'confirmed':
          status = (
            <StatusCell icon="done" color="success" status="Confirmed" />
          );
          break;
        case 'pending':
          status = (
            <StatusCell icon="schedule" color="warning" status="Pending" />
          );
          break;
        case 'failed':
          status = <StatusCell icon="close" color="error" status="Failed" />;
          break;
        default:
          status = <StatusCell icon="info" color="info" status={value} />;
      }
      return status;
    },
  },
  {
    Header: 'Actions',
    accessor: 'actions',
    Cell: ({ row }: { row: { original: PaymentStatusResponse } }) => {
      return (
        <MDBox>
          <PaymentDetailsButton payment={row.original} />
        </MDBox>
      );
    },
  },
];

const setupDataTable = (transactions: PaymentStatusResponse[]) => {
  const rows = transactions.map(
    (transaction: PaymentStatusResponse, index: number) => ({
      id: index + 1,
      createdAt: transaction.createdAt,
      status: transaction.status,
      paymentId: transaction.paymentIdentifier || '',
      currency: transaction.currency,
      amount: transaction.amount,
      actions: transaction.paymentId,
      original: transaction, // Pass the full transaction object
    })
  );

  return {
    columns,
    rows,
  };
};

export default setupDataTable;
